/**
 * Shared styling utilities for consistent theming across components
 */

export const getReadOnlyFieldStyle = (currentTheme: any) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: currentTheme.accent,
    color: currentTheme.mutedForeground,
    fontFamily: '"DM Sans", sans-serif',
    '& fieldset': {
      borderColor: currentTheme.border,
    },
    '&:hover fieldset': {
      borderColor: currentTheme.border,
    },
    '&.Mui-focused fieldset': {
      borderColor: currentTheme.border,
    },
  },
});

export const getFieldLabelStyle = (currentTheme: any) => ({
  fontSize: '1rem',
  fontWeight: 600,
  mb: 1,
  display: 'block',
  color: currentTheme.foreground,
  fontFamily: '"DM Sans", sans-serif',
});

export const getMutedTextStyle = (currentTheme: any) => ({
  color: currentTheme.mutedForeground,
  fontFamily: '"DM Sans", sans-serif',
});

export const getErrorAlertStyle = () => ({
  mb: 2,
});

export const getDescriptiveTextStyle = (currentTheme: any) => ({
  mb: 3,
});


export const getAutocompleteInputStyle = (currentTheme: any) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: currentTheme.card,
    color: currentTheme.foreground,
    "& fieldset": {
      borderColor: currentTheme.border,
    },
    "&:hover fieldset": {
      borderColor: currentTheme.mutedForeground,
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b3fff",
    },
  },
  "& .MuiInputLabel-root": {
    color: currentTheme.mutedForeground,
    fontFamily: '"DM Sans", sans-serif',
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#4b3fff",
  },
  "& .MuiInputBase-input": {
    color: currentTheme.foreground,
    fontFamily: '"DM Sans", sans-serif',
  },
  "& .MuiInputBase-input::placeholder": {
    color: currentTheme.mutedForeground,
    opacity: 1,
  },
});

// For the Autocomplete component itself (input styling)
export const getAutocompleteDropdownStyle = (currentTheme: any) => ({
  "& .MuiAutocomplete-option": {
    color: currentTheme.foreground, // Use theme text color by default
    "&:hover": {
      backgroundColor: "rgba(75, 63, 255, 0.08)",
      color: "#4b3fff",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(75, 63, 255, 0.08)",
      color: "#4b3fff",
    },
  },
});

export const getAutocompletePaperStyle = (currentTheme: any) => ({
  backgroundColor: currentTheme.card,
  color: currentTheme.foreground, 
  border: `1px solid rgba(0, 0, 0, 0.15)`, 
  borderRadius: 2,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)",
});

export const getAutocompletePopperStyle = () => ({
  "& .MuiAutocomplete-paper": {
    border: `2px solid #4b3fff`,
    borderRadius: 2,
    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(75, 63, 255, 0.2)`,
  },
});

export const getNavLinkStyle = (currentTheme: any) => ({
  // Override ListItem defaults
  padding: 0,
  margin: 0,
  minHeight: "auto",
  
  // Custom nav link styling
  display: "block",
  width: "100%",
  py: 1,
  px: 1.5,
  mx: 0.5,
  borderRadius: 1,
  textDecoration: "none",
  color: currentTheme.foreground,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    width: "3px",
    height: "100%",
    backgroundColor: "#4b3fff",
    opacity: 0,
    borderRadius: "0 2px 2px 0",
  },
  
  "&:focus": {
    outline: "none",
  },
  "&:focus-visible": {
    outline: "2px solid #4b3fff",
    outlineOffset: "2px",
    animation: "focusPulse 2s ease-in-out infinite",
  },
  
  // Hover state - light blurple 
  "&:hover": {
    backgroundColor: "rgba(75, 63, 255, 0.08)",
    color: "#4b3fff",
    boxShadow: "0 4px 12px rgba(75, 63, 255, 0.15)",
    transform: "translateY(-1px)",
  },
  
  "&:active": {
    backgroundColor: "rgba(75, 63, 255, 0.2)",
    transform: "scale(0.98) translateY(0)",
    transition: "all 0.1s ease-in-out",
    boxShadow: "0 2px 6px rgba(75, 63, 255, 0.2)",
  },
  
  // Active state 
  "&.active": {
    backgroundColor: "rgba(75, 63, 255, 0.12)",
    color: "#4b3fff",
    boxShadow: "0 6px 20px rgba(75, 63, 255, 0.25)",
    
    // Show the indicator
    "&::before": {
      opacity: 1,
    },
    
    "&:hover": {
      backgroundColor: "rgba(75, 63, 255, 0.18)",
      boxShadow: "0 8px 25px rgba(75, 63, 255, 0.3)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      backgroundColor: "rgba(75, 63, 255, 0.25)",
      transform: "scale(0.98) translateY(0)",
      boxShadow: "0 4px 15px rgba(75, 63, 255, 0.35)",
    },
  },
  
  "@keyframes focusPulse": {
    "0%, 100%": {
      boxShadow: "0 0 0 2px rgba(75, 63, 255, 0.3)",
    },
    "50%": {
      boxShadow: "0 0 0 4px rgba(75, 63, 255, 0.1)",
    },
  },
});

// TextField styles for URL inputs and other form fields
export const getTextFieldStyle = (currentTheme: any) => ({
  '& .MuiInputBase-input': {
    color: currentTheme.foreground,
    fontFamily: '"DM Sans", sans-serif',
    '&::placeholder': {
      color: currentTheme.mutedForeground,
      opacity: 0.4,
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: currentTheme.card,
    '& fieldset': {
      borderColor: currentTheme.border,
    },
    '&:hover fieldset': {
      borderColor: currentTheme.mutedForeground,
    },
    '&.Mui-focused fieldset': {
      borderColor: currentTheme.primary,
    },
  },
});

// Disabled TextField styles
export const getDisabledTextFieldStyle = (currentTheme: any) => ({
  '& .MuiInputBase-input': {
    color: currentTheme.mutedForeground,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: currentTheme.card,
    '& fieldset': {
      borderColor: currentTheme.border,
    },
  },
});

// Section title styles
export const getSectionTitleStyle = (currentTheme: any) => ({
  fontWeight: 500,
  mb: 1,
  color: currentTheme.primary,
  fontFamily: '"DM Sans", sans-serif',
});

// Error banner styles
export const getErrorBannerStyle = (currentTheme: any) => ({
  mt: 2,
  p: 2,
  backgroundColor: '#fef2f2',
  border: '1px solid #fca5a5',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
});

// Error icon styles
export const getErrorIconStyle = () => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: '#dc2626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: 0.1,
});

// Success status indicator styles
export const getSuccessStatusStyle = () => ({
  mt: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
});

// Success icon styles
export const getSuccessIconStyle = () => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: '#10b981',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

// Success text styles
export const getSuccessTextStyle = () => ({
  color: '#10b981',
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
});

// Form control styles for selects
export const getFormControlStyle = (currentTheme: any) => ({
  backgroundColor: currentTheme.card,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: currentTheme.border,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: currentTheme.mutedForeground,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: currentTheme.primary,
  },
  '& .MuiSelect-select': {
    color: 'white',
    fontFamily: '"DM Sans", sans-serif',
  },
});

// Menu item styles
export const getMenuItemStyle = (currentTheme: any) => ({
  color: currentTheme.foreground,
  fontFamily: '"DM Sans", sans-serif',
});

// Input label styles
export const getInputLabelStyle = (currentTheme: any) => ({
  color: currentTheme.foreground,
  fontFamily: '"DM Sans", sans-serif',
});

// Table container styles
export const getTableContainerStyle = (currentTheme: any) => ({
  border: `1px solid ${currentTheme.border}`,
  borderRadius: '8px',
  backgroundColor: currentTheme.background,
  '& .MuiTableHead-root': {
    backgroundColor: currentTheme.accent,
  },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${currentTheme.border}`,
  },
});

// Table header cell styles
export const getTableHeaderCellStyle = (currentTheme: any) => ({
  fontWeight: 600,
  fontSize: "1rem",
  fontFamily: '"DM Sans", sans-serif',
  color: currentTheme.foreground,
  backgroundColor: currentTheme.accent,
  borderBottom: `1px solid ${currentTheme.border}`,
});

// Table data cell styles
export const getTableDataCellStyle = (currentTheme: any) => ({
  color: currentTheme.foreground,
  borderBottom: `1px solid ${currentTheme.border}`,
  fontFamily: '"DM Sans", sans-serif',
});

// Muted table data cell styles
export const getMutedTableDataCellStyle = (currentTheme: any) => ({
  color: currentTheme.mutedForeground,
  borderBottom: `1px solid ${currentTheme.border}`,
  fontFamily: '"DM Sans", sans-serif',
});

// Icon button styles
export const getIconButtonStyle = (currentTheme: any) => ({
  opacity: 1,
  '&:hover': {
    color: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)'
  }
});

// Table row styles for events with/without permissions
export const getEventTableRowStyle = (currentTheme: any, hasPermission: boolean) => ({
  opacity: hasPermission === false ? 0.6 : 1,
  backgroundColor: hasPermission === false ? 'rgba(251, 146, 60, 0.05)' : 'transparent',
  '&:hover': {
    backgroundColor: hasPermission === false 
      ? 'rgba(251, 146, 60, 0.1)' 
      : currentTheme.accent,
  }
});

// Caption text styles
export const getCaptionTextStyle = (currentTheme: any) => ({
  color: currentTheme.foreground,
  mt: 1,
  display: 'block',
  fontFamily: '"DM Sans", sans-serif',
});

// Link styles
export const getLinkStyle = (currentTheme: any) => ({
  color: currentTheme.primary,
  textDecoration: 'none',
  fontFamily: '"DM Sans", sans-serif',
});
